import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Hash, MessageSquare, Search, Trash2 } from "lucide-react";
import { openWS } from "@/lib/ws";
import ReportDialog from "@/components/ReportDialog";

export function RoomsList() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "", category: "general" });

  const load = () => api.get("/rooms", { params: { q: q || undefined, category: category === "all" ? undefined : category } }).then((r) => setRooms(r.data));
  useEffect(() => { api.get("/rooms/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, category]);

  const create = async () => {
    if (!user) return toast.error("Sign in first");
    if (!form.name || !form.slug) return toast.error("Name & slug required");
    try {
      await api.post("/rooms", form);
      setOpen(false);
      setForm({ name: "", slug: "", description: "", category: "general" });
      load();
      const cats = await api.get("/rooms/categories");
      setCategories(cats.data);
      toast.success("Room created");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary">// 04 · chat rooms</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Discuss. Debate. Decide.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Topic-scoped rooms across every branch of eco-engineering.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-room-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />New room</Button></DialogTrigger>
          <DialogContent className="max-w-md rounded-none">
            <DialogHeader><DialogTitle>Create a room</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label><Input data-testid="room-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Slug</Label><Input data-testid="room-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="mt-2 rounded-none bg-input mono" placeholder="battery-recycling" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label><Input data-testid="room-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="Energy · Materials · Mobility …" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label><Textarea data-testid="room-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="room-submit" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex items-center gap-2 border border-border px-3 py-2">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input data-testid="room-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rooms — 'battery', 'carbon', 'aquaponics'…" className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground" />
        {q && <button onClick={() => setQ("")} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">clear</button>}
      </div>

      <div className="mb-8 flex flex-wrap gap-1">
        <Chip active={category === "all"} onClick={() => setCategory("all")} testid="chip-all">All</Chip>
        {categories.map((c) => (
          <Chip key={c} active={category === c} onClick={() => setCategory(c)} testid={`chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</Chip>
        ))}
      </div>

      {rooms.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground">No rooms match your filter. Try clearing search or another category.</div>
      ) : (
        <div className="border border-border">
          {rooms.map((r, i) => (
            <Link key={r.id} to={`/rooms/${r.slug}`} data-testid={`room-${r.slug}`} className={`flex items-center justify-between p-5 hover:bg-primary/5 transition-colors ${i > 0 ? "border-t border-border" : ""}`}>
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center border border-primary/40 text-primary"><Hash className="h-4 w-4" /></div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-lg tracking-tight">{r.name}</div>
                    {r.category && r.category !== "general" && <span className="border border-border px-2 py-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">{r.category}</span>}
                  </div>
                  <div className="text-sm text-muted-foreground">{r.description}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="mono text-primary">{r.thread_count}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">threads</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
      active ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
    }`}
  >
    {children}
  </button>
);

export function RoomView() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [replies, setReplies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [reply, setReply] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [wsLive, setWsLive] = useState(false);

  const load = () => {
    api.get(`/rooms/${slug}`).then((r) => setRoom(r.data));
    api.get(`/threads`, { params: { room_slug: slug } }).then((r) => setThreads(r.data));
  };
  useEffect(() => { load(); setActive(null); /* eslint-disable-next-line */ }, [slug]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => {});
  }, [user]);

  // Realtime: subscribe to room WS. Polling remains as fallback when WS is closed.
  useEffect(() => {
    const token = localStorage.getItem("eco_token");
    const conn = openWS(`/rooms/${slug}`, (data) => {
      if (data?.type === "thread" && data.thread) {
        setThreads((prev) => (prev.some((t) => t.id === data.thread.id) ? prev : [data.thread, ...prev]));
      } else if (data?.type === "reply" && data.reply) {
        setActive((cur) => {
          if (cur && cur.id === data.thread_id) {
            setReplies((prev) => (prev.some((r) => r.id === data.reply.id) ? prev : [...prev, data.reply]));
          }
          return cur;
        });
        setThreads((prev) => prev.map((t) => t.id === data.thread_id ? { ...t, reply_count: (t.reply_count || 0) + 1 } : t));
      }
    }, { token, onOpen: () => setWsLive(true), onClose: () => setWsLive(false) });
    return () => conn.close();
  }, [slug]);

  // Polling fallback — only when WS is not live
  useEffect(() => {
    if (wsLive || active) return;
    const t = setInterval(() => {
      api.get(`/threads`, { params: { room_slug: slug } }).then((r) => setThreads(r.data)).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [slug, active, wsLive]);
  useEffect(() => {
    if (wsLive || !active) return;
    const t = setInterval(() => {
      api.get(`/replies/${active.id}`).then((r) => setReplies(r.data)).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [active, wsLive]);

  const openThread = async (t) => {
    setActive(t);
    const r = await api.get(`/replies/${t.id}`);
    setReplies(r.data);
  };

  const create = async () => {
    if (!user) return toast.error("Sign in");
    if (!form.title || !form.body) return toast.error("Title + body required");
    try {
      await api.post("/threads", { room_slug: slug, ...form });
      setOpen(false);
      setForm({ title: "", body: "" });
      load();
      toast.success("Thread posted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const postReply = async () => {
    if (!user) return toast.error("Sign in");
    if (reply.length < 2) return;
    try {
      await api.post("/replies", { thread_id: active.id, body: reply });
      setReply("");
      openThread(active);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const deleteThread = async (t) => {
    if (!window.confirm(`Delete thread "${t.title}"? All replies will also be removed.`)) return;
    try {
      await api.delete(`/admin/thread/${t.id}`);
      toast.success("Thread deleted");
      if (active?.id === t.id) setActive(null);
      setThreads((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const deleteReply = async (r) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/admin/reply/${r.id}`);
      toast.success("Reply deleted");
      setReplies((prev) => prev.filter((x) => x.id !== r.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!room) return <div className="mx-auto max-w-6xl px-6 py-24 text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <Link to="/rooms" className="mono text-xs text-muted-foreground hover:text-primary">← all rooms</Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-primary"><Hash className="h-5 w-5" /><h1 className="text-4xl font-black tracking-tighter">{room.name}</h1></div>
          <p className="mt-2 text-muted-foreground">{room.description}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-thread-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />New thread</Button></DialogTrigger>
          <DialogContent className="max-w-lg rounded-none">
            <DialogHeader><DialogTitle>Start a thread</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label><Input data-testid="thread-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
              <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Body</Label><Textarea data-testid="thread-body" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="thread-submit" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Post</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {active ? (
        <div className="mt-8">
          <button onClick={() => setActive(null)} className="mono text-xs text-muted-foreground hover:text-primary">← back to threads</button>
          <div className="mt-4 border border-border p-6">
            <div className="flex items-center justify-between text-xs">
              <span className="font-bold">{active.author_name} <span className="text-primary uppercase tracking-widest">· {active.author_role}</span></span>
              <div className="flex items-center gap-2">
                {user && <ReportDialog targetType="thread" targetId={active.id} compact />}
                {isAdmin && (
                  <button
                    data-testid={`admin-delete-thread-${active.id}`}
                    onClick={() => deleteThread(active)}
                    className="flex items-center gap-1 border border-destructive/60 px-2 py-1 text-[10px] uppercase tracking-widest text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <Trash2 className="h-3 w-3" />Delete
                  </button>
                )}
                <span className="mono text-muted-foreground">{new Date(active.created_at).toLocaleString()}</span>
              </div>
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">{active.title}</h2>
            <p className="mt-3 whitespace-pre-wrap leading-relaxed">{active.body}</p>
          </div>
          <div className="mt-6">
            <div className="flex items-center gap-2 text-sm uppercase tracking-widest text-primary">
              <MessageSquare className="h-4 w-4" />Replies ({replies.length})
              {wsLive && <span className="ml-2 mono text-[9px] text-primary/70">● live</span>}
            </div>
            {user && (
              <div className="mt-4 border border-border p-4">
                <Textarea data-testid="reply-body" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="rounded-none bg-input mb-3" />
                <Button onClick={postReply} data-testid="reply-submit" size="sm" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Reply</Button>
              </div>
            )}
            <div className="mt-4 space-y-px bg-border">
              {replies.map((r) => (
                <div key={r.id} data-testid={`reply-${r.id}`} className="bg-background p-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold">{r.author_name} <span className="text-primary uppercase tracking-widest">· {r.author_role}</span></span>
                    <div className="flex items-center gap-2">
                      {user && <ReportDialog targetType="reply" targetId={r.id} compact />}
                      {isAdmin && (
                        <button
                          data-testid={`admin-delete-reply-${r.id}`}
                          onClick={() => deleteReply(r)}
                          className="flex items-center gap-1 border border-destructive/60 px-2 py-1 text-[10px] uppercase tracking-widest text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                      <span className="mono text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{r.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 border border-border">
          {threads.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No threads yet — start the first one.</div>
          ) : threads.map((t, i) => (
            <button key={t.id} data-testid={`thread-${t.id}`} onClick={() => openThread(t)} className={`w-full text-left p-5 hover:bg-primary/5 transition-colors ${i > 0 ? "border-t border-border" : ""}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold tracking-tight">{t.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">by {t.author_name} · {new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="mono text-primary">{t.reply_count}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">replies</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
